<template>
  <section
    v-if="status !== 'available' && status !== 'information'"
    class="notification-summary tw:grid tw:gap-3 tw:rounded-(--radius-note) tw:bg-(--surface-input) tw:p-3.5 tw:wrap-anywhere"
    :aria-label="t('notifications.summary')"
  >
    <div class="tw:flex tw:items-start tw:gap-2.5">
      <AppIcon :name="icon" size="action" class="tw:mt-0.5 tw:flex-none tw:text-(--info-ink)" />
      <div class="tw:min-w-0 tw:grid tw:gap-1.5">
        <p
          v-if="status === 'completed' && response"
          class="tw:type-caption tw:text-muted-foreground"
        >
          {{ t('notifications.response') }}
        </p>
        <p class="notification-summary__heading tw:type-body-strong">{{ heading }}</p>
        <p v-if="description" class="tw:type-secondary">{{ description }}</p>
        <p v-if="status === 'expired' && response" class="tw:type-secondary">
          {{ t('notifications.lastResponse') }} {{ t('notifications.outcomes.' + response.action) }}
        </p>
        <template v-if="response">
          <p class="notification-summary__actor tw:type-caption tw:text-muted-foreground">
            <span v-if="response.actorName"
              >{{ t('notifications.recordedBy') }} {{ response.actorName }} · </span
            >{{ t('notifications.actors.' + response.actorType) }}
          </p>
          <time
            :datetime="response.actionTimeRaw"
            class="notification-summary__time tw:type-caption tw:text-muted-foreground"
            >{{ response.actionTime }}</time
          >
        </template>
      </div>
    </div>
    <div v-if="recordedDates.length" class="tw:grid tw:gap-1.5">
      <h3 class="tw:type-caption tw:text-muted-foreground">
        {{ t('notifications.dialysisDates') }}
      </h3>
      <p class="tw:type-body">
        {{ recordedDates.map((date) => DateUtils.formatWeekdayDate(date)).join(' · ') }}
      </p>
    </div>
    <div v-if="recordedNote" class="tw:grid tw:gap-1.5">
      <h3 class="tw:type-caption tw:text-muted-foreground">
        {{ t('notifications.recordedNote') }}
      </h3>
      <p class="tw:type-body tw:whitespace-pre-wrap">{{ recordedNote }}</p>
    </div>
  </section>
</template>
<script setup>
// 1. Import necessary components
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DateUtils from '@/shared/utilities/date-utils';
import AppIcon from '@/shared/components/AppIcon.vue';
import { NOTIFICATION_ACTION, NOTIFICATION_STATE } from '../constants/notification';
import { NOTIFICATION_PRESENTATION } from '../constants/presentation';

// 2. Use
const props = defineProps({ notification: { type: Object, required: true } });
const { t } = useI18n();

// 3. Define a reactive variable
// 4. Define methods or computed properties if needed
const response = computed(() => props.notification.acceptedResponse);
const definition = computed(() => NOTIFICATION_PRESENTATION[props.notification.code]?.response);
const status = computed(() => {
  if (props.notification.state === NOTIFICATION_STATE.COMPLETED) {
    return 'completed';
  }
  if (props.notification.state === NOTIFICATION_STATE.CANCELLED) {
    return 'cancelled';
  }
  if (props.notification.expired) {
    return 'expired';
  }
  if (props.notification.deferred) {
    return 'snoozed';
  }
  if (!props.notification.actionable) {
    return 'pending';
  }
  if (props.notification.allowedActions.length) {
    return 'available';
  }
  return 'information';
});
const icon = computed(() => {
  if (status.value === 'completed') {
    return 'check';
  }
  if (status.value === 'pending' || status.value === 'snoozed' || status.value === 'expired') {
    return 'clock';
  }
  return 'info';
});
const heading = computed(() => {
  if (response.value && status.value === 'completed') {
    const label =
      definition.value?.labels?.[response.value.details.request?.[definition.value.field]];
    if (label) {
      return t(label);
    }
    const outcome = definition.value?.outcomes?.[response.value.action];
    if (outcome) {
      return t(outcome);
    }
    return t('notifications.outcomes.' + response.value.action);
  }
  return t('notifications.states.' + status.value);
});
const description = computed(() => {
  if (status.value === 'completed') {
    if (response.value?.action === NOTIFICATION_ACTION.DISMISSED) {
      return t('notifications.closedDescription');
    }
    return '';
  }
  if (status.value === 'snoozed') {
    return t('notifications.nextReminder', {
      time: DateUtils.formatServerDateTime(response.value.details.snoozedUntil),
    });
  }
  if (status.value === 'pending') {
    return t('notifications.notDueDescription');
  }
  if (status.value === 'expired' || status.value === 'cancelled') {
    return t('notifications.readOnlyDescription');
  }
  return '';
});
const recordedDates = computed(() => {
  if (!definition.value?.datesField || !response.value) {
    return [];
  }
  return response.value.details.request?.[definition.value.datesField] ?? [];
});
const recordedNote = computed(() => {
  if (!definition.value?.noteField || !response.value) {
    return '';
  }
  return response.value.details.request?.[definition.value.noteField] ?? '';
});

// 5. VueJS Hooks
</script>
